import React, { useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Tooltip
} from "@mui/material";
import axiosClient from "../../api/axiosClient";

// Colori per la percentuale del giocatore (intera riga)
const getRowColor = (percentage: number) => {
    if (percentage < 20) return "#bbdefb";
    if (percentage < 40) return "#90caf9";
    if (percentage < 60) return "#ffe082";
    if (percentage < 80) return "#ffb74d";
    return "#ff8a65";
};

// Icone impiego
const getUsageIcon = (index: number) => {
    if (index < -0.10) return <span style={{ color: "#d32f2f" }}>▼</span>;
    if (index < 0.05) return <span style={{ color: "#fbc02d" }}>■</span>;
    return <span style={{ color: "#388e3c" }}>▲</span>;
};

interface MatchInfo {
    id: number;
    label: string;
    date: string;
}

interface PlayerUsage {
    playerId: number;
    playerName: string;
    setsPerMatch: Record<number, number>;
    total: number;
    percentage: number;
    trainingSetsPresent: number;
}

interface UsageTableResponse {
    matches: MatchInfo[];
    players: PlayerUsage[];
    maxSetInAnyMatch: number;
    trainingTotalSets: number;
}

export default function UsageTable({ tournamentId }: { tournamentId: number | null }) {
    const [data, setData] = useState<UsageTableResponse | null>(null);
    const [loading, setLoading] = useState(true);

    const [sortBy, setSortBy] =
        useState<"name" | "total" | "percentage" | "usage">("percentage");
    const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");

    useEffect(() => {
        axiosClient
            .get("/usage/getTable", { params: { tournamentId } })
            .then(res => setData(res.data))
            .finally(() => setLoading(false));
    }, [tournamentId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" mt={4}>
                <CircularProgress />
            </Box>
        );
    }

    if (!data) return <Typography>Errore nel caricamento.</Typography>;

    // 👉 Ordinamento
    const sortedPlayers = [...data.players].sort((a, b) => {
        // calcoliamo l’indice impiego (match% – training%)
        const trainingA = a.trainingSetsPresent / data.trainingTotalSets;
        const matchA = a.percentage / 100;
        const usageA = matchA - trainingA;

        const trainingB = b.trainingSetsPresent / data.trainingTotalSets;
        const matchB = b.percentage / 100;
        const usageB = matchB - trainingB;

        let valueA: any;
        let valueB: any;

        switch (sortBy) {
            case "name":
                valueA = a.playerName.toLowerCase();
                valueB = b.playerName.toLowerCase();
                break;
            case "total":
                valueA = a.total;
                valueB = b.total;
                break;
            case "percentage":
                valueA = a.percentage;
                valueB = b.percentage;
                break;
            case "usage":
                valueA = usageA;
                valueB = usageB;
                break;
        }

        if (valueA < valueB) return sortDir === "asc" ? -1 : 1;
        if (valueA > valueB) return sortDir === "asc" ? 1 : -1;
        return 0;
    });

    const toggleSort = (
        field: "name" | "total" | "percentage" | "usage"
    ) => {
        if (sortBy === field) {
            setSortDir(sortDir === "asc" ? "desc" : "asc");
        } else {
            setSortBy(field);
            setSortDir("asc");
        }
    };

    const headerStyle = { cursor: "pointer", userSelect: "none" };

    return (
        <TableContainer component={Paper} sx={{ mt: 1 }}>
            <Table
                size="small"
                sx={{
                    "& th": { fontSize: 13, padding: "2px 4px", whiteSpace: "nowrap" },
                    "& td": { fontSize: 12, padding: "2px 4px", whiteSpace: "nowrap" }
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell onClick={() => toggleSort("name")} sx={headerStyle}>
                            <strong>
                                Giocatore {sortBy === "name" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                            </strong>
                        </TableCell>

                        {data.matches.map((m, index) => (
                            <TableCell key={m.id} align="center">
                                <Tooltip
                                    title={
                                        <Box>
                                            <div><strong>{m.label}</strong></div>
                                            <div>{m.date}</div>
                                        </Box>
                                    }
                                    placement="top"
                                >
                                    <strong>{index + 1}</strong>
                                </Tooltip>
                            </TableCell>
                        ))}

                        <TableCell
                            align="center"
                            onClick={() => toggleSort("total")}
                            sx={headerStyle}
                        >
                            <strong>
                                Tot {sortBy === "total" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                            </strong>
                        </TableCell>

                        <TableCell
                            align="center"
                            onClick={() => toggleSort("percentage")}
                            sx={headerStyle}
                        >
                            <strong>
                                % {sortBy === "percentage" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                            </strong>
                        </TableCell>

                        <TableCell
                            align="center"
                            onClick={() => toggleSort("usage")}
                            sx={headerStyle}
                        >
                            <strong>
                                Impiego {sortBy === "usage" ? (sortDir === "asc" ? "▲" : "▼") : ""}
                            </strong>
                        </TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {sortedPlayers.map(p => {
                        const trainingPct = p.trainingSetsPresent / data.trainingTotalSets;
                        const matchPct = p.percentage / 100;
                        const index = matchPct - trainingPct;

                        return (
                            <TableRow
                                key={p.playerId}
                                sx={{ backgroundColor: getRowColor(p.percentage) }}
                            >
                                <TableCell>
                                    <Tooltip
                                        placement="right"
                                        title={
                                            <Box>
                                                <div><strong>Tot Set Partita:</strong> {p.total}</div>
                                                <div><strong>% Partita:</strong> {p.percentage.toFixed(1)}%</div>
                                                <div><strong>Set Allenamento Presenti:</strong> {p.trainingSetsPresent}/{data.trainingTotalSets}</div>
                                            </Box>
                                        }
                                    >
                                        <strong>{p.playerName}</strong>
                                    </Tooltip>
                                </TableCell>

                                {data.matches.map(m => {
                                    const value = p.setsPerMatch[m.id] ?? 0;
                                    return (
                                        <TableCell key={m.id} align="center">
                                            {value}
                                        </TableCell>
                                    );
                                })}

                                <TableCell align="center">
                                    <strong>{p.total}</strong>
                                </TableCell>

                                <TableCell align="center">
                                    {p.percentage.toFixed(1)}%
                                </TableCell>

                                <TableCell align="center">
                                    {getUsageIcon(index)} {(index * 100).toFixed(1)}%
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
